import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { TrussEnvelope } from '@paragon/contract-library';
import { Point2D } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../../components/hooks';
import { convertFromDesignToLayoutCoordinates } from '../../../library/layout-geometry/coordinates';
import { TrussEnvelopeRunCalculator } from '../../../library/layout-geometry/truss-envelopes/bulk-creation-tools/runs';
import { useAppMonitor } from '../../../monitor';
import { createTrussEnvelopes } from '../../../store/actions/layout/trussEnvelopeActions';
import { selectItemsByGuid } from '../../../store/actions/selectActions';
import { getAllTrussEnvelopeNames } from '../../../store/selectors/layoutDataSelectors';
import { getGeometricEcosystemForTrussEnvelopeInsertion } from '../../../store/selectors/layoutGeometricEcosystemSelectors';
import { is3DLayoutCameraInUse } from '../../../store/selectors/layoutModeSelectors';
import { clearInsertingObject, InsertingTrussEnvelopeRunInfo } from '../../../store/slices/applicationSlice';
import { useMousePositionInScene } from '../../hooks/useMousePositionInScene';
import { threeJsDom } from '../../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../../util/events/types';
import { INSERTION_ENVELOPE_PROPS } from './TrussEnvelopeInsertionVisual';
import { TrussEnvelopeVisual } from './TrussEnvelopeVisual';

export function RunInsertionVisuals() {
  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);
  return insertingObjectInfo?.type === 'trussEnvelopeRun' ? <RunVisual data={insertingObjectInfo.data} /> : null;
}

function RunVisual(props: { data: InsertingTrussEnvelopeRunInfo['data'] }) {
  const dispatch = useAppDispatch();
  const monitor = useAppMonitor();

  const mousePosition = useMousePositionInScene();

  const trussEnvelopes = useAppSelector((state) => state.layout.trussEnvelopes);
  const baseEnvelope = trussEnvelopes[props.data.baseEnvelopeGuid];

  const in3D = useAppSelector(is3DLayoutCameraInUse);

  const ecosystem = useAppSelector(getGeometricEcosystemForTrussEnvelopeInsertion);

  const trussEnvelopeNames = useAppSelector(getAllTrussEnvelopeNames);

  // we want to freeze envelopes when saving them so the preview visuals don't change while the task spinner is ongoing
  const [savingEnvelopes, setSavingEnvelopes] = useState<TrussEnvelope[] | null>(null);

  const calculateRunEnvelopes = useCallback(
    (stoppingPoint: Point2D) => {
      if (ecosystem == null) return null;

      const calculator = new TrussEnvelopeRunCalculator(baseEnvelope, props.data.spacing, stoppingPoint);

      const result = props.data.calculateEnvelopes
        ? calculator.calculateRunEnvelopes(ecosystem, trussEnvelopeNames)
        : calculator.createRunEnvelopesWithoutCalculation(trussEnvelopeNames);

      return Array.from(result).filter(notNullish);
    },
    [baseEnvelope, ecosystem, trussEnvelopeNames, props.data.calculateEnvelopes, props.data.spacing],
  );

  const previewedEnvelopes = useMemo(() => {
    if (mousePosition == null) return null;

    const stoppingPoint = convertFromDesignToLayoutCoordinates(mousePosition).projectTo2D();
    return calculateRunEnvelopes(stoppingPoint);
  }, [mousePosition, calculateRunEnvelopes]);

  useEffect(() => {
    function onClick(event: GlobalThreeJSDomObjectEvent) {
      const stoppingPoint = convertFromDesignToLayoutCoordinates(event.eventPosition.worldPosition).projectTo2D();
      const envelopesInRun = calculateRunEnvelopes(stoppingPoint);

      if (envelopesInRun == null) return;

      setSavingEnvelopes(envelopesInRun);

      dispatch(createTrussEnvelopes(envelopesInRun))
        .then(({ success }) => {
          if (success && envelopesInRun.length) {
            dispatch(
              selectItemsByGuid(
                envelopesInRun.map(({ guid }) => guid),
                'trussEnvelope',
              ),
            );
          }
        })
        .finally(() => {
          dispatch(clearInsertingObject());

          // we will be unmounted now that insertion is over; don't worry about resetting `savingEnvelopes`
          monitor.recordInteraction('Truss run tool used');
        });

      return false;
    }

    threeJsDom.addGlobalListener('click', onClick);
    return () => void threeJsDom.removeGlobalListener('click', onClick);
  }, [calculateRunEnvelopes, dispatch, monitor]);

  if (savingEnvelopes == null && previewedEnvelopes == null) return null;

  // FIXME: this approach where a single component is responsible for all aspects of calculating and displaying the
  //   envelopes in the run was a deliberate decision to reduce complexity, but it does mean that we can't include the
  //   previewed envelopes in the automatic run dimensions.
  //
  // I believe this is the right approach for now, but feel free to reconsider in the future.

  return (savingEnvelopes ?? previewedEnvelopes)!.map((envelope, i) => (
    <TrussEnvelopeVisual inert {...INSERTION_ENVELOPE_PROPS} in3D={in3D} envelope={envelope} key={i} />
  ));
}
